import { errorText, get } from '@/api/client';
import { forgetOp, newOpId, refusedText, rememberOp } from '@/api/createOps';
/**
 * The shift drawer: the fields POST and PUT /admin/shifts read, nothing else.
 * Times are Oslo wall-clock in the form and become ISO instants on save
 * (osloISO). Approval goes through the same PUT and is refused by the API
 * without both punches, so the switch is disabled until they exist.
 */
import {
  type ApiAbsence,
  type ApiShift,
  type ApiShiftType,
  type ApiTaskAssignment,
  type ApiUser,
  type Shift,
  type ShiftWrite,
  useShiftWrites,
  useWorkTimeChanges,
} from '@/api/data';
import {
  projectLabel,
  setShiftProject,
  useProjectRequired,
  useProjectWord,
  useProjects,
  useProjectsOn,
} from '@/api/projects';
import { invalidateShifts } from '@/api/shifts';
import { useCan } from '@/auth/caps';
import { T, useT } from '@/i18n';
import { type Entry, breakPolicy, oslo, osloISO, ruleText, typeTime } from '@/rules';
import { setDirty, useContextStore, useDirty } from '@/shell/context';
import {
  type Choice,
  answerLeaving,
  answeredInline,
  canSaveAll,
  saveAll,
  useInlineLeave,
  useNeutralSave,
} from '@/shell/leave';
import { useTenant } from '@/shell/tenant';
import { Button, Drawer, toast } from '@/ui';
import { DateInput } from '@/ui/DateInput';
import { validPeriod } from '@/ui/DateJump';
import { Tasks } from '@/ui/Tasks';
import { TimeInput } from '@/ui/TimeInput';
import { MONTHS, fmtD, fullName, num, shiftSpan } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { trapTab } from '@/ui/page';
import { useQueryClient } from '@tanstack/react-query';
import { addDays, workedHoursAt } from '@timefly/core';
import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { createPortal } from 'react-dom';
import History from '../arbeidstid/History';
import lk from './ClockedLock.module.css';
import {
  Edges,
  LunchRow,
  type LunchValue,
  type Outcome,
  Overlap,
  TaskMoves,
  lunchBase,
  lunchWrite,
} from './Panel';
import { PersonOptions } from './PersonOptions';
import {
  NO_EDITS,
  TaskEditor,
  type TaskEdits,
  defOf,
  freeSlot,
  hasEdits,
  realEdits,
  taskProblemAt,
  tasksBodyOf,
} from './TaskEditor';
import type { Option } from './advice';
import { UNKNOWN, refused } from './lock';
import type { PanelContext } from './model';

/** What the editor opens on: a task, a new activity row, and a range dragged or drawn on the day lane. */
export type EditFocus = {
  /** the opener asked for these clock times ("Legg inn ut-tid"): the fold opens on them, and saving without them is refused */
  punch?: 'in' | 'out' | 'both';
  task?: number;
  add?: boolean;
  start?: string;
  end?: string;
  /** a warning's way in (issue #153 SP29): the editor opens on the section the warning is about */
  section?: 'vakt' | 'stempling' | 'oppgaver' | 'notat';
  /** a second task opened with it: the other side of a divider moved on the lane */
  also?: { task: number; start?: string; end?: string };
};

export interface DraftShift {
  id: number | null;
  user_id: number | null;
  shift_type_id: number | null;
  date: string;
  from: string;
  to: string;
  description: string;
  published: boolean;
  is_approved: boolean;
  /** on a new shift the project it is made with (#115); on a saved one read only, set through ProjectField (2026-09-29) */
  project_id?: number | null;
  punchIn: string;
  punchOut: string;
  lunchIn: string;
  lunchOut: string;
  /**
   * The stored instants behind those four wall clocks. A punch is an instant,
   * and HH:mm cannot carry its seconds or its date, so the original is kept
   * here and sent back untouched whenever the field still shows it.
   */
  /** the stored day, so a clocked shift's day change is known (2026-09-27) */
  date0?: string;
  /** put the clock and break times on the shift's own day, keeping their times (2026-09-27) */
  anchor?: boolean;
  punchIn0: string | null;
  punchOut0: string | null;
  lunchIn0: string | null;
  lunchOut0: string | null;
  /** a date the manager set for the clock-in or clock-out, over the one worked out from the times (owner 2026-10-28) */
  punchInDay?: string;
  punchOutDay?: string;
  /** the plan's end date when the manager set it, over the one worked out from the times: 22:00 to 03:00 ends the next day (BK, 2026-10-29) */
  toDay?: string;
}

/** The plan's end date worked out from the times: at or before the start is the next day. */
export const endDayOf = (date: string, from: string, to: string) =>
  to && from && to <= from ? addDays(date, 1) : date;
const dayGap = (a: string, b: string) => Math.round((Date.parse(b) - Date.parse(a)) / 864e5);

/** A clock-in as the lock line says it: "14.9.2026 kl. 08:00". */
const stamp = (iso: string) => {
  const o = oslo(iso);
  return o ? T('{day} kl. {time}', { day: fmtD(o.date), time: o.time }) : '';
};

export function draftOf(s: Shift): DraftShift {
  return {
    id: s.id,
    user_id: s.user_id,
    shift_type_id: s.shift_type_id,
    date: oslo(s.start_time)?.date ?? '',
    from: oslo(s.start_time)?.time ?? '',
    to: oslo(s.end_time)?.time ?? '',
    // a shift longer than the times say (two days and more) keeps its end date
    ...(() => {
      const end = oslo(s.end_time)?.date;
      const start = oslo(s.start_time);
      return end && start && endDayOf(start.date, start.time, oslo(s.end_time)?.time ?? '') !== end
        ? { toDay: end }
        : {};
    })(),
    description: s.description ?? '',
    published: s.published,
    is_approved: s.is_approved,
    project_id: s.project_id ?? null,
    punchIn: oslo(s.punch_in_time)?.time ?? '',
    punchOut: oslo(s.punch_out_time)?.time ?? '',
    lunchIn: oslo(s.lunch_in_time)?.time ?? '',
    lunchOut: oslo(s.lunch_out_time)?.time ?? '',
    date0: oslo(s.start_time)?.date ?? '',
    punchIn0: s.punch_in_time ?? null,
    punchOut0: s.punch_out_time ?? null,
    lunchIn0: s.lunch_in_time ?? null,
    lunchOut0: s.lunch_out_time ?? null,
  };
}
export function newDraft(date: string, userId: number | null, type: ApiShiftType | undefined): DraftShift {
  return {
    id: null,
    user_id: userId,
    shift_type_id: type?.id ?? null,
    date,
    from: typeTime(type?.start_time) || '08:00',
    to: typeTime(type?.end_time) || '16:00',
    description: '',
    published: false,
    is_approved: false,
    punchIn: '',
    punchOut: '',
    lunchIn: '',
    lunchOut: '',
    punchIn0: null,
    punchOut0: null,
    lunchIn0: null,
    lunchOut0: null,
  };
}

/** The API body for a draft. A time at or before `from` is the next day. */
/**
 * @param may what the role holds: `punches` is timesheet.edit, `approval` is
 * timesheet.approve. The server checks the fields a body would change, so sending a punch
 * it did not touch is harmless; leaving it out anyway is the same rule from the other side,
 * and keeps the request to what this person may write.
 */
/** Whether the planned shift has ended by `now`: an end at or before the start is the next day's. */
export const endsBy = (d: Pick<DraftShift, 'date' | 'from' | 'to'>, now: number) =>
  Date.parse(osloISO(d.to <= d.from ? addDays(d.date, 1) : d.date, d.to)) <= now;

export function bodyOf(
  d: DraftShift,
  may: { punches: boolean; approval: boolean } = { punches: true, approval: true },
): ShiftWrite | { problem: string } {
  /** The planned times: a wall clock the operator types, and at or before `from` is the next day. */
  const at = (time: string, after?: string) => {
    if (!time) return null;
    const day = after && time <= after ? addDays(d.date, 1) : d.date;
    return osloISO(day, time);
  };
  /**
   * A recorded punch is an instant. If the field still shows the stored one, send
   * that instant back exactly, seconds and date included: approving a shift, or
   * editing something else on it, must not rewrite the times somebody clocked.
   * Only a field whose displayed time no longer matches is rebuilt, and then it
   * is anchored to its own original date rather than the drawer's, so an
   * overnight or multiday punch keeps its shape (TF-PUNCH-PRESERVE, 2026-09-16).
   */
  const punch = (shown: string, original: string | null) => {
    if (!shown) return null;
    // "Flytt stemplingen til vaktens dag": the shift's day, or the next for a time before the clock-in
    if (d.anchor) return osloISO(d.punchIn && shown < d.punchIn ? addDays(d.date, 1) : d.date, shown);
    if (original && oslo(original)?.time === shown) return original;
    return osloISO((original && oslo(original)?.date) || d.date, shown);
  };
  const nextDay = (iso: string) => {
    const o = oslo(iso);
    return o ? osloISO(addDays(o.date, 1), o.time) : iso;
  };
  /** Typed over, or newly typed onto a field that held nothing. */
  const edited = (shown: string, original: string | null) =>
    !!shown && !(original && oslo(original)?.time === shown);
  const ms = (iso: string | null) => (iso == null ? null : Date.parse(iso));

  const inEdited = edited(d.punchIn, d.punchIn0);
  const outEdited = edited(d.punchOut, d.punchOut0);
  // The ambiguity an operator can see is the minute on screen, whichever end they typed:
  // two equal minutes could mean nothing worked or a whole day, and the drawer must not
  // pick one. An untouched pair is never questioned, so a genuine 24-hour punch still saves.
  if ((inEdited || outEdited) && d.punchIn && d.punchOut && d.punchIn === d.punchOut)
    return {
      problem: T(
        'Stemplet inn og stemplet ut viser samme klokkeslett. Skriv tidene slik at lengden på vakten går fram.',
      ),
    };

  const punchIn = d.punchInDay && d.punchIn ? osloISO(d.punchInDay, d.punchIn) : punch(d.punchIn, d.punchIn0);
  let punchOut =
    d.punchOutDay && d.punchOut ? osloISO(d.punchOutDay, d.punchOut) : punch(d.punchOut, d.punchOut0);
  const inMs = ms(punchIn);
  const outMs = () => ms(punchOut) as number;
  // An edited end belongs to the clock-in's day, or the next morning when it is before the start: a
  // clock-out stamped days late (forgotten, then pressed on the next shift) is fixed by typing the time,
  // not kept on the wrong date (owner 2026-10-28: "they can only change time, they can't change date").
  // The start is never moved to fit it.
  if (punchOut != null && punchIn != null && outEdited && !d.punchOutDay) {
    punchOut = osloISO(oslo(punchIn)?.date ?? d.date, d.punchOut);
    if ((ms(punchOut) as number) <= (inMs as number)) punchOut = nextDay(punchOut);
  }
  // an edit that still leaves the pair out of order is refused, rather than repaired by
  // rewriting the endpoint nobody touched
  if (
    punchOut != null &&
    inMs != null &&
    (inEdited || outEdited || !!d.punchInDay || !!d.punchOutDay) &&
    outMs() <= inMs
  )
    return { problem: T('Stemplet inn må være før stemplet ut.') };

  const breakAfterStart = (shown: string, original: string | null) => {
    const iso = punch(shown, original);
    if (iso != null && inMs != null && edited(shown, original) && (ms(iso) as number) < inMs)
      return nextDay(iso);
    return iso;
  };
  const lunchIn = breakAfterStart(d.lunchIn, d.lunchIn0);
  const lunchOut = breakAfterStart(d.lunchOut, d.lunchOut0);
  if (
    lunchIn != null &&
    lunchOut != null &&
    (edited(d.lunchIn, d.lunchIn0) || edited(d.lunchOut, d.lunchOut0)) &&
    (ms(lunchOut) as number) <= (ms(lunchIn) as number)
  )
    return { problem: T('Pause til må være etter pause fra.') };

  // a shift needs a start; a cleared field says so instead of saving nothing (#272 M-01)
  if (!d.from) return { problem: T('Skriv når vakten begynner.') };
  if (d.toDay && d.to && d.from && osloISO(d.toDay, d.to) <= osloISO(d.date, d.from))
    return { problem: T('Til dato og tid må være etter fra.') };

  return {
    user_id: d.user_id,
    shift_type_id: d.shift_type_id,
    description: d.description || null,
    start_time: osloISO(d.date, d.from),
    end_time: d.toDay && d.to ? osloISO(d.toDay, d.to) : (at(d.to, d.from) ?? osloISO(d.date, d.from)),
    ...(may.punches
      ? {
          punch_in_time: punchIn,
          punch_out_time: punchOut,
          lunch_in_time: lunchIn,
          lunch_out_time: lunchOut,
        }
      : {}),
    published: d.published,
    ...(may.approval ? { is_approved: d.is_approved } : {}),
    // a new shift is made with its project (#115); a saved one keeps ProjectField's own save
    ...(d.id == null && d.project_id != null ? { project_id: d.project_id } : {}),
  };
}

/**
 * Whether saving this draft changes registered time: a clock or break time that was set, now
 * typed over or cleared. The server then requires a reason (work-time history, 2026-09-21);
 * filling a time that was empty does not.
 */
export function correctsTime(d: DraftShift): boolean {
  const pairs: [string, string | null][] = [
    [d.punchIn, d.punchIn0],
    [d.punchOut, d.punchOut0],
    [d.lunchIn, d.lunchIn0],
    [d.lunchOut, d.lunchOut0],
  ];
  const movedDay = (day: string | undefined, original: string | null) =>
    !!day && !!original && oslo(original)?.date !== day;
  return (
    pairs.some(([shown, original]) => !!original && oslo(original)?.time !== shown) ||
    movedDay(d.punchInDay, d.punchIn0) ||
    movedDay(d.punchOutDay, d.punchOut0)
  );
}

/**
 * The reason for deleting a shift that holds registered time: undefined when none is needed,
 * null when the person cancelled, else the text. Shared by the drawer, the card menu and the
 * bulk delete, so every door asks the same question.
 */
export function askDeleteReason(s: {
  punch_in_time?: string | null;
  punch_out_time?: string | null;
}): string | null | undefined {
  if (!s.punch_in_time && !s.punch_out_time) return undefined;
  const r = window.prompt(T('Vakten har registrert tid. Hvorfor slettes den?'));
  if (r == null) return null;
  if (r.trim().length < 3) {
    toast(T('Skriv en grunn på minst tre tegn.'));
    return null;
  }
  return r.trim();
}

/** What is typed in an open editor, beyond the draft it opened on. */
type Session = {
  d: DraftShift | null;
  edits: TaskEdits;
  open: Set<SectionId>;
  reason: string;
  unlocked: boolean;
  pickPerson: boolean;
  bad: Bad;
  /** the date field's text while it is not a real date (half typed, or 31.02); null when it is one (UI01-15) */
  dateText: string | null;
};
type Bad = { at: 'reason' | 'project' | 'task'; msg: string } | null;
/**
 * The phone list and the desktop split hold the editor in different places, so crossing the
 * 560 px breakpoint (a resize, a rotation, zoom) remounts it (review of #155, F1). The editor keeps
 * its session here; the one arriving for the same draft takes it back, so a resize is never a
 * discard. Keyed on the draft object itself: a new opening is a new object.
 */
let kept: { draft: DraftShift | null; s: Session } | null = null;
/**
 * The shift follows the click (owner, 2026-10-20): a new shift's editor opened on another cell
 * keeps what was typed, with that cell's day and person. Call before the page opens `next`.
 * A prospect picked a person, clicked Saturday, and saved the untouched draft for today instead.
 */
export function carryTo(next: DraftShift): boolean {
  const was = kept?.s;
  const from = kept?.draft;
  // a create already sent, or with an unknown answer, stays with its own opening: never sent twice
  if (!from || !was?.d || was.d.id != null || from.id != null || outs.has(from)) return false;
  carried = {
    draft: next,
    s: { ...was, d: { ...was.d, date: next.date, user_id: next.user_id }, dateText: null },
  };
  return true;
}
/** the session carryTo handed to its next opening, taken by that opening's reset */
let carried: { draft: DraftShift; s: Session } | null = null;
/**
 * A write out from an editor for one opening, keyed on the opening's draft object (reviews of #155,
 * R1 and F1). `out` while it is; `unknown` when no answer said whether the server applied it (no
 * answer, 5xx). Both outlive a remount, so the editor arriving in the other layout neither sends it
 * again nor offers to. A refusal or a success removes it. A create carries an operation id (#157),
 * kept in `body`: «Sjekk status» sends that same operation again, which the server answers with
 * the shift it made or makes once, so it is safe where a new attempt is not.
 */
type Out = {
  state: 'out' | 'unknown';
  body: ShiftWrite;
  /** a «Sjekk planen» read is out; `gen` counts them, so only the latest one's answer is used */
  checking?: boolean;
  gen?: number;
};
const outs = new WeakMap<DraftShift, Out>();
const UNKNOWN_NEW =
  'Vi vet ikke om vakten ble opprettet. Sjekk status: det sender samme lagring på nytt, og lager aldri en vakt til.';
const outListeners = new Set<() => void>();
const setOut = (draft: DraftShift, o: Out | null) => {
  if (o) outs.set(draft, o);
  else outs.delete(draft);
  for (const f of outListeners) f();
};
const onOuts = (f: () => void) => {
  outListeners.add(f);
  return () => void outListeners.delete(f);
};
/** The openings an editor shows now (F2): a write that completes closes only its own opening. */
const showing = new Map<DraftShift, number>();

/**
 * The shift type, full width. A closed select cannot wrap, so a type whose name and times do not fit
 * (a long or translated name at a 280 px panel) is written out under it as well, and is the select's
 * title; nothing about the choice is hidden (#154 VF-07).
 */
function TypeSelect({
  value,
  types,
  disabled,
  onChange,
}: {
  value: number | null;
  types: ApiShiftType[];
  disabled: boolean;
  onChange: (id: number | null) => void;
}) {
  const ref = useRef<HTMLSelectElement>(null);
  const [cut, setCut] = useState(false);
  const labelOf = (x: ApiShiftType) => `${x.name} ${typeTime(x.start_time)}–${typeTime(x.end_time)}`;
  const chosen = types.find((x) => x.id === value);
  const label = chosen ? labelOf(chosen) : T('Ingen type');
  useLayoutEffect(() => {
    const el = ref.current;
    const pen = typeof document !== 'undefined' ? document.createElement('canvas').getContext?.('2d') : null;
    if (!el || !pen) return;
    const fit = () => {
      const cs = getComputedStyle(el);
      pen.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
      // the room for the text: the box less its padding (the chevron sits in the right padding)
      const room = el.clientWidth - Number.parseFloat(cs.paddingLeft) - Number.parseFloat(cs.paddingRight);
      setCut(pen.measureText(label).width > room);
    };
    fit();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [label]);
  return (
    <>
      <select
        ref={ref}
        className="inp w"
        aria-label={T('Vakttype')}
        title={label}
        disabled={disabled}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
      >
        <option value="">{T('Ingen type')}</option>
        {types.map((x) => (
          <option key={x.id} value={x.id}>
            {labelOf(x)}
          </option>
        ))}
      </select>
      {cut && (
        <span className="hint" data-testid="vedit-type-full">
          {label}
        </span>
      )}
    </>
  );
}

type SectionId = 'vakt' | 'stempling' | 'oppgaver' | 'notat' | 'historikk' | 'flere';
/** Which sections an opening starts with: Vakt, or Stempling for a clock time, or Oppgaver for an activity. */
/**
 * A new shift opened as already published is a registration of worked time, not a plan («Legg til
 * registrering» in Kontroll and in Uka): it shows Stempling from the start and needs a person and both
 * clock times (owner's walk-through 2026-10-21, #191: it opened as «Ny vakt» with no clock fields).
 */
const isRegistration = (d?: DraftShift | null) => !!d && d.id == null && !!d.published;
const opening = (focus?: EditFocus, registration = false): Set<SectionId> => {
  const s = new Set<SectionId>();
  if (registration) s.add('stempling');
  if (focus?.section) s.add(focus.section);
  else if (focus?.task != null || focus?.add) s.add('oppgaver');
  else s.add('vakt');
  if (focus?.punch) s.add('stempling');
  return s;
};
type IconName = Parameters<typeof Icon>[0]['name'];
const initialsOf = (u: ApiUser) =>
  `${(u.name ?? '').charAt(0)}${(u.lastname ?? '').charAt(0)}`.toUpperCase() || '?';
/** The planned length in hours; an end at or before the start is the next day. */
const planHours = (from: string, to: string) => {
  const m = (x: string) => Number(x.slice(0, 2)) * 60 + Number(x.slice(3, 5));
  const len = m(to) - m(from);
  return Math.round(((len <= 0 ? len + 1440 : len) / 60) * 10) / 10;
};

/** What the page knows around a saved shift, and its own actions: every write goes its usual way (issue #153). */
export interface ShiftActions {
  /** the shift as stored: publication, approval, tasks and edges are read from it */
  shift?: ApiShift | undefined;
  around?: Pick<PanelContext, 'absence' | 'offer' | 'swap' | 'sameDay'> | undefined;
  busy?: boolean;
  outcome?: Outcome | null | undefined;
  lunch?:
    | {
        minutes: number;
        preset?: { start: string; minutes: number; n: number } | null | undefined;
        onLunch: (start: string | null, minutes: number | null) => Promise<boolean>;
      }
    | undefined;
  onDecide?: ((kind: 'offer' | 'swap', id: number, ok: boolean) => void) | undefined;
  onOpenAbsence?: ((a: ApiAbsence) => void) | undefined;
  onCover?: (() => void) | undefined;
  /** `saved`: the shift as the save answered it, so the approval binds to the saved revision */
  onApprove?: ((v: boolean, saved?: ApiShift) => void) | undefined;
  onPublish?: (() => void) | undefined;
  onUnpublish?: (() => void) | undefined;
  onFree?: (() => void) | undefined;
  onAbsence?: (() => void) | undefined;
  onPick?: (() => void) | undefined;
  onMoveTask?: ((assignmentId: number, toShiftId: number) => void) | undefined;
  onBoundary?: ((earlierId: number, laterId: number, at: string) => void) | undefined;
}

export function ShiftDrawer({
  draft,
  entries,
  types,
  users,
  tasks,
  lunch,
  onClose,
  onDelete,
  onDuplicate,
  inline = false,
  focus,
  submit,
  locked = false,
  onReread,
  others = [],
  match,
  planning = true,
  act,
}: {
  /** false in «Uka» (#118): no publication, planned lunch, task or edge moves, or cover */
  planning?: boolean;
  /** the shift as stored and what the page knows around it, with the page's own actions (issue #153) */
  act?: ShiftActions | undefined;
  draft: DraftShift | null;
  entries: Entry[];
  types: ApiShiftType[];
  users: ApiUser[];
  /** the shift's stored tasks, live from the shift list; their status saves on its own (TF-TASK-CONTROLS) */
  tasks?: ApiTaskAssignment[] | undefined;
  /** the stored planned lunch, so a task over it is said in the overlap note */
  lunch?: { from: string; to: string } | null | undefined;
  onClose: () => void;
  onDelete: (id: number, reason?: string) => void;
  /** the card menu's "Dupliser til i morgen", offered here too because the card menu needs hover or a keyboard (TF-REVIEW-CLOSURE-20260915-01) */
  onDuplicate?: ((id: number) => void) | undefined;
  /** V19 (review V02): the same form inside the page's panel, beside the plan, instead of a modal drawer */
  inline?: boolean;
  /** opened from the day lane: that task open for editing, or a new activity row; the rest stay collapsed */
  focus?: EditFocus | undefined;
  /**
   * The page's lock for writes to an existing shift (retest R02): the save runs under it, so a
   * save cannot race another write to the same shift or overwrite one whose outcome is unknown.
   * Resolves true once saved; the page says what happened.
   */
  submit?: ((id: number, f: () => Promise<unknown>) => Promise<boolean>) | undefined;
  /** another write to this shift is out, or its outcome is unknown: saving waits for a read */
  locked?: boolean;
  onReread?: (() => void) | undefined;
  /** the other shifts touching this shift's day, for "Andre i tidsrommet" beside an activity */
  others?: ApiShift[] | undefined;
  /** Hugin's ranking for a new shift at the draft's day and times (advice.ts ranked, issue #131) */
  match?: ((d: DraftShift) => Option[]) | undefined;
}) {
  const t = useT();
  const { tenant } = useTenant();
  const policy = breakPolicy(tenant);
  const w = useShiftWrites();
  const qc = useQueryClient();
  // the task editor is the manager's; the same capability that lets the page open the drawer for editing
  const can = useCan();
  const canEditTasks = can('vaktplan.endre');
  // since issue #153 every opening of a shift is this editor, so a role that may not plan reads the
  // plan's fields and edits only what it holds (punches, approval): no control the server would refuse
  const canPlan = can('vaktplan.endre');
  // Three authorities on one drawer, the same three the server checks per field: planning
  // the shift, correcting what was clocked, and approving the hours. A role with one of
  // them is not offered the other two (docs/design/2026-09-16-custom-roles.md).
  const canSeePunches = can('timeliste.se');
  const canEditPunches = can('timeliste.rette');
  const projectsOn = useProjectsOn();
  const projectRequired = useProjectRequired();
  const projectWord = useProjectWord();
  const projects = useProjects().data ?? [];
  const canApprove = can('timeliste.godkjenne');
  const back = useRef(draft && kept?.draft === draft ? kept.s : null);
  // the draft a session was taken back for: the reset below leaves it as typed
  const restored = useRef(back.current ? draft : undefined);
  const [d, setD] = useState<DraftShift | null>(back.current?.d ?? draft);
  // staged task definitions (TF-WEB-COMPLETION A); nothing is written until the shift's own save
  const [edits, setEdits] = useState<TaskEdits>(back.current?.edits ?? NO_EDITS);
  // the sections (issue #153): Vakt open on an ordinary opening; Stempling when opened to put in a
  // clock time (issue #81); Oppgaver when opened on an activity
  const [open, setOpen] = useState<Set<SectionId>>(
    () => back.current?.open ?? opening(focus, isRegistration(draft)),
  );
  const openSection = (id: SectionId, on: boolean) =>
    setOpen((o) => {
      const n = new Set(o);
      if (on) n.add(id);
      else n.delete(id);
      return n;
    });
  const punchOpen = open.has('stempling');
  const setPunchOpen = (v: boolean) => openSection('stempling', v);
  // the person on a held shift is changed on request only (SP17)
  const [pickPerson, setPickPerson] = useState(back.current?.pickPerson ?? false);
  // the pause is the shift's own field, saved with it (owner 2026-10-25): what the fields hold, from the
  // stored plan, or a lunch dragged on the day lane
  const lunchShift = act?.lunch && canEditTasks && planning ? act.shift : undefined;
  const lunchStart = (): LunchValue => {
    const p = act?.lunch?.preset;
    if (p) return { at: oslo(p.start)?.time ?? '', len: String(p.minutes) };
    return lunchShift ? lunchBase(lunchShift) : { at: '', len: '' };
  };
  const [pause, setPause] = useState<LunchValue>(lunchStart);
  const [lunchProblem, setLunchProblem] = useState<{ field: 'at' | 'len'; text: string } | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new shift, a new stored plan or a new drag starts over
  useEffect(() => {
    setPause(lunchStart());
    setLunchProblem(null);
  }, [
    lunchShift?.id,
    lunchShift?.planned_lunch_start,
    lunchShift?.planned_lunch_minutes,
    act?.lunch?.preset?.n,
  ]);
  const lunchOut = lunchShift ? lunchWrite(lunchShift, pause) : {};
  const lunchDirty = !!lunchOut.body || !!lunchOut.problem;
  // a lunch dragged on the day lane is edited where the lunch row is: Vakt opens
  // biome-ignore lint/correctness/useExhaustiveDependencies: once per drag
  useEffect(() => {
    if (act?.lunch?.preset) openSection('vakt', true);
  }, [act?.lunch?.preset?.n]);
  const [askClose, setAskClose] = useState(false);
  const [reason, setReason] = useState(back.current?.reason ?? '');
  // a clocked shift keeps its day unless the manager unlocks it on purpose (owner, 2026-09-27)
  const [unlocked, setUnlocked] = useState(back.current?.unlocked ?? false);
  // a refused save says where (review F2): the section holding the field opens, the error stays
  // beside the field, and the field takes the focus
  const [bad, setBad] = useState<Bad>(back.current?.bad ?? null);
  // the date field's text while it is not a real date (UI01-15). The draft keeps the last real date,
  // since every instant the editor builds on each render is made from it; the text is part of the
  // session, so it is an unsaved edit, it survives a fold of Vakt and the move across the phone
  // breakpoint, and saving waits for a real date. A new opening, a discard or a real date clears it.
  const [dateText, setDateText] = useState<string | null>(back.current?.dateText ?? null);
  const badDate = dateText !== null;
  // time fields whose text is not a complete time yet (#272 A-S01): the draft keeps the last good time,
  // and nothing saves until each one is a time again
  const [badTimes, setBadTimes] = useState<Record<string, boolean>>({});
  const badTime = Object.values(badTimes).some(Boolean);
  const timeBad = useCallback(
    (k: string) => (b: boolean) => setBadTimes((s) => (!!s[k] === b ? s : { ...s, [k]: b })),
    [],
  );
  // a write for this opening still out or unknown, possibly sent by the editor before a remount
  const out = useSyncExternalStore(onOuts, () => (draft ? (outs.get(draft)?.state ?? null) : null));
  const inFlight = out !== null;
  const checking = useSyncExternalStore(onOuts, () => !!(draft && outs.get(draft)?.checking));
  useEffect(() => {
    if (!draft) return;
    showing.set(draft, (showing.get(draft) ?? 0) + 1);
    return () => {
      const n = (showing.get(draft) ?? 1) - 1;
      if (n > 0) showing.set(draft, n);
      else showing.delete(draft);
    };
  }, [draft]);
  // any edit answers the error; one brought back by a remount stays until then
  const seen = useRef({ d, reason, edits });
  useEffect(() => {
    const was = seen.current;
    if (was.d !== d || was.reason !== reason || was.edits !== edits) setBad(null);
    seen.current = { d, reason, edits };
  });
  // working on an activity, the shift's own fields fold away (handoff §4, retest R06): Vakt closed
  const shiftOpen = open.has('vakt');
  const setShiftOpen = (v: boolean) => openSection('vakt', v);
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new draft resets; focus comes with it
  useEffect(() => {
    // taken back after a remount (F1): what was typed stays
    if (draft && draft === restored.current) return;
    restored.current = undefined;
    // carried to another cell (carryTo): what was typed stays, on the new day and person
    // (kept, not cleared: React runs this twice on a development mount, and a new opening is a new object)
    const moved = draft && carried?.draft === draft ? carried.s : null;
    if (moved) {
      setD(moved.d);
      setEdits(moved.edits);
      setOpen(moved.open);
      setReason(moved.reason);
      setUnlocked(moved.unlocked);
      setPickPerson(moved.pickPerson);
      setBad(moved.bad);
      setDateText(moved.dateText);
      setAskClose(false);
      return;
    }
    setReason('');
    setDateText(null);
    setD(draft);
    // a range dragged or drawn on the day lane arrives as the rows' new times, to be saved here
    const times = (f?: { start?: string; end?: string }) =>
      f?.start || f?.end
        ? {
            has_time_range: true,
            ...(f.start ? { start_time: f.start } : {}),
            ...(f.end ? { end_time: f.end } : {}),
          }
        : {};
    const range = times(focus);
    const changed: TaskEdits['changed'] = {};
    for (const [id, f] of [
      [focus?.task, focus],
      [focus?.also?.task, focus?.also],
    ] as const) {
      const t = id != null ? tasks?.find((a) => a.id === id) : undefined;
      if (t) changed[t.id] = { ...defOf(t), ...times(f) };
    }
    setEdits({
      changed,
      added: focus?.add
        ? [
            {
              name: '',
              description: '',
              has_time_range: true,
              // no range given (a + without a place): the first free half hour, so Fra and Til are
              // filled rather than empty (gap A2)
              ...(freeSlot(draft?.from ?? '', draft?.to ?? '', [
                ...(tasks ?? []).filter((t) => t.start_time && t.end_time).map(defOf),
                ...(lunch ? [{ start_time: lunch.from, end_time: lunch.to }] : []),
              ]) ?? { start_time: '', end_time: '' }),
              ...range,
              key: 1,
            },
          ]
        : [],
    });
    // a lunch dragged on the lane arrives with the shift: its row is in Vakt
    const first = opening(focus, isRegistration(draft));
    if (act?.lunch?.preset) first.add('vakt');
    setOpen(first);
    setPickPerson(false);
    setAskClose(false);
  }, [draft]);
  // written after every render, not on unmount: the editor arriving in the other layout renders
  // before the leaving one is removed, so it reads what this one last showed
  useEffect(() => {
    if (draft) kept = { draft, s: { d, edits, open, reason, unlocked, pickPerson, bad, dateText } };
  });
  // opened to put in a clock time, the editor starts in that field (issue #81, mock: the correction
  // starts with the actual end focused)
  const punchInput = (k: 'in' | 'out') =>
    bodyRef.current?.querySelector<HTMLInputElement>(
      `[aria-label="${k === 'in' ? 'Stemplet inn' : 'Stemplet ut'}"]`,
    ) ?? null;
  const punchFocused = useRef<DraftShift | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: the field exists once both folds are open and the permission is read
  useEffect(() => {
    if (!focus?.punch || punchFocused.current === draft) return;
    const el = punchInput(focus.punch === 'in' ? 'in' : 'out');
    if (!el) return;
    el.focus();
    punchFocused.current = draft;
  }, [draft, shiftOpen, punchOpen, canEditPunches]); // the inputs appear with the permission, which may load after the editor
  // unsaved work in this editor holds a company switch until the person decides (TF-COMPANY-SWITCH),
  // and is asked about before the drawer closes by any route (TF-SWITCH-RECOVERY)
  // a row opened but not changed, or an empty new row, is not an edit
  const real = realEdits(tasks ?? [], edits);
  const dirty =
    !!d &&
    !!draft &&
    (JSON.stringify(d) !== JSON.stringify(draft) ||
      hasEdits(real) ||
      dateText !== null ||
      badTime ||
      lunchDirty);
  const saveRef = useRef<() => Promise<boolean>>(async () => false);
  // inline, the heading takes focus when the editor opens, as the panel's does (review F05), unless it
  // was opened to put in a clock time: that field has it (the effect above runs first, so this one
  // took it away again; issue #146, Rett stemplet inn)
  const headRef = useRef<HTMLHeadingElement | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new draft is a new editor to move to
  useEffect(() => {
    if (inline && !focus?.punch) headRef.current?.focus({ preventScroll: true });
    // on a phone the editor is in the list under its shift: brought into view (issue #153)
    if (inline) headRef.current?.closest('.pphone .vedit')?.scrollIntoView?.({ block: 'start' });
  }, [draft]);
  // opened on an activity, the editor's own scroll starts at that activity's form, its times in
  // view, rather than wherever the name field's focus left it (retest R06)
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const toForm = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new draft is a new activity to show
  useEffect(() => {
    // only an editor opened on an activity scrolls to it
    toForm.current = focus?.task != null || !!focus?.add;
  }, [draft]);
  // the form renders one pass after the draft, so this waits for it, once
  useEffect(() => {
    const box = bodyRef.current;
    const form = box?.querySelector<HTMLElement>('[data-testid=task-form]');
    if (!toForm.current || !inline || !box || !form || shiftOpen) return;
    toForm.current = false;
    box.scrollTop = form.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop - 8;
  });
  // one registration with the shell (issue #72): the leave question asks on every exit, inside the
  // page or away from it, and may save for the manager only when the edit decides nothing; a change
  // to Publisert or Godkjent is the manager's own decision (web-manager.md section 3)
  useDirty('shift-drawer', dirty, T('Vakt'));
  const decides = !!d && !!draft && (d.published !== draft.published || d.is_approved !== draft.is_approved);
  const neutral = useCallback(() => saveRef.current(), []);
  useNeutralSave('shift-drawer', dirty && !decides ? neutral : null);
  // the leave question is asked in this panel, not as a dialog over the plan (owner, 2026-10-20)
  useInlineLeave('shift-drawer');
  const leaving = useContextStore((st) => st.leaving);
  const noteRef = useRef<HTMLDivElement | null>(null);
  /** the last field focused in the editor, where focus goes back when the leave question closes */
  const lastFocus = useRef<HTMLElement | null>(null);
  const keepFocus = (e: { target: EventTarget }) => {
    if (e.target instanceof HTMLElement) lastFocus.current = e.target;
  };
  // closing the question without leaving puts focus back where the work was, not on <body> (#272 A-T01)
  const wasAsking = useRef(false);
  const asked = askClose || (!!leaving && answeredInline());
  useEffect(() => {
    const was = wasAsking.current;
    wasAsking.current = asked;
    if (!was || asked) return;
    const at = document.activeElement;
    if (at && at !== document.body && at.isConnected) return;
    const back =
      (lastFocus.current?.isConnected ? lastFocus.current : null) ??
      bodyRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]');
    back?.focus();
  });
  const writeOpen = useContextStore((st) => st.pendingWrites > 0 || st.unknownWrites > 0);
  /** true when the close was intercepted: the drawer stays open and the question is shown */
  const askFirst = () => {
    if (!dirty) return false;
    setAskClose(true);
    return true;
  };
  // the shift's own history (ADR 0021): what the punch hint promises is kept, shown. Called
  // before the early return below, so the hook order never changes between renders.
  const mayReadHistory = can('timeliste.se') && d?.id != null;
  const historyQ = useWorkTimeChanges({ shift_id: d?.id ?? undefined }, mayReadHistory);
  if (!d) return null;
  const isNew = d.id == null;
  const registration = isRegistration(draft);
  const user = users.find((u) => u.id === d.user_id);
  const punched = !!(d.punchIn && d.punchOut);
  const set = <K extends keyof DraftShift>(k: K, v: DraftShift[K]) => setD({ ...d, [k]: v });
  const setType = (id: number | null) => {
    const ty = types.find((x) => x.id === id);
    setD({
      ...d,
      shift_type_id: id,
      from: typeTime(ty?.start_time) || d.from,
      to: typeTime(ty?.end_time) || d.to,
    });
  };
  // full-shift hours after the company's break policy, on the instants the save would send; null until the policy is known
  // built once: the hours preview and the inline warning are the same answer, so they
  // cannot disagree, and the warning stays on screen instead of only appearing on save
  const built = bodyOf(d, { punches: canEditPunches, approval: canApprove });
  const punchProblem = 'problem' in built ? built.problem : null;
  // registered time moved: the history needs a reason (work-time history, 2026-09-21)
  const corrects = !isNew && canEditPunches && correctsTime(d);
  const clocked = !isNew && Boolean(d.punchIn0);
  const movesDay = clocked && Boolean(d.date0) && d.date !== d.date0;
  // clock times on another date than the shift (an end after midnight is fine): shown, with a fix
  const dayOfIso = (v: string | null) => (v ? (oslo(v)?.date ?? null) : null);
  const punchDay = dayOfIso(d.punchIn0);
  const offDay =
    clocked &&
    !d.anchor &&
    !movesDay &&
    ((punchDay !== null && punchDay !== d.date) ||
      [d.punchOut0, d.lunchIn0, d.lunchOut0].some((v) => {
        const x = dayOfIso(v);
        return x !== null && x !== d.date && x !== addDays(d.date, 1);
      }));
  // a clock-in more than 12 h from the planned start: the server asks for a reason (#217), so the editor does too
  const farClock =
    !registration &&
    !('problem' in built) &&
    !!built.punch_in_time &&
    Math.abs(Date.parse(built.punch_in_time) - Date.parse(built.start_time)) > 12 * 3600_000;
  const needsReason = corrects || movesDay || Boolean(d.anchor) || farClock;
  // the clock-out's date, said when it is not the clock-in's day: the field shows only a time, so a clock-out
  // pressed days late was invisible (owner 2026-10-28); typing the time puts it on the clock-in's day
  const outDayNote = (() => {
    if ('problem' in built || !built.punch_in_time || !built.punch_out_time) return null;
    const a = oslo(built.punch_in_time)?.date;
    const b = oslo(built.punch_out_time)?.date;
    if (!a || !b || a === b) return null;
    const days = Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
    return days === 1 ? (
      <p className="vsecnote" data-testid="out-day">
        {T('Utstemplet neste dag, {day}.', { day: fmtD(b) })}
      </p>
    ) : (
      <p className="vsecnote warn" data-testid="out-day">
        {T(
          'Utstemplet {day}, {n} dager etter innstemplingen. Skriv riktig tid i Ut, så legges den på innstemplingsdagen.',
          {
            day: fmtD(b),
            n: days,
          },
        )}
      </p>
    );
  })();
  const worked =
    punched && policy && !('problem' in built)
      ? workedHoursAt(
          {
            punchIn: built.punch_in_time,
            punchOut: built.punch_out_time,
            breakIn: built.lunch_in_time,
            breakOut: built.lunch_out_time,
            // the planned end, so a paid §10-9(2) break stays worked time (#252)
            plannedEnd: built.end_time,
          },
          policy,
        )
      : null;

  /**
   * Saves the draft. A correction to hours that were approved takes the approval away, so an
   * approval is never kept for times nobody approved (SP10). `approve`: «Lagre og godkjenn», two
   * requests: the save, then, only when it went through, the approval of the shift the save answered
   * with, so an approval binds to saved values (#149 V3, owner 2026-10-29).
   */
  /** Opens `sec`, focuses the first match of `pick` in it once mounted, and says `msg`. */
  const reveal = (sec: SectionId, pick: string, msg: string, at?: 'reason' | 'project' | 'task') => {
    toast(msg);
    setBad(at ? { at, msg } : null);
    openSection(sec, true);
    requestAnimationFrame(() => {
      const box = bodyRef.current?.querySelector(`[data-testid=vsec-${sec}] .vsecb`);
      const el =
        box?.querySelector<HTMLElement>(pick) ??
        box?.querySelector<HTMLElement>('input:not(:disabled), select, button');
      el?.focus();
    });
  };
  const badMsg = (at: 'reason' | 'project' | 'task') =>
    bad?.at === at ? (
      <p className="ferr" id={`vbad-${at}`} role="alert" data-testid={`vbad-${at}`}>
        {bad.msg}
      </p>
    ) : null;
  const badProps = (at: 'reason' | 'project') =>
    bad?.at === at ? { 'aria-invalid': true, 'aria-describedby': `vbad-${at}` } : {};

  async function save(opts: { approve?: boolean } = {}): Promise<boolean> {
    if (!d) return false;
    // worked time belongs to someone, from a clock-in to a clock-out
    if (registration && d.user_id == null) {
      toast(T('Velg hvem som jobbet.'));
      setShiftOpen(true);
      return false;
    }
    if (registration && !(d.punchIn && d.punchOut)) {
      toast(T('Skriv inn når det ble stemplet inn og ut.'));
      setPunchOpen(true);
      return false;
    }
    if (draft?.is_approved && corrects && !canApprove) {
      toast(T('Timene er godkjent. Be noen som kan godkjenne timer om å rette dem.'));
      return false;
    }
    // «Prosjekt påkrevd» (Innstillinger → Prosjekter): a new shift needs one before it is saved
    if (d.id == null && projectRequired && d.project_id == null) {
      reveal('notat', 'select', T('Velg {what} før vakten lagres.', { what: projectWord.lower }), 'project');
      return false;
    }
    const taskProblem = canEditTasks ? taskProblemAt(tasks ?? [], edits) : null;
    if (taskProblem) {
      // the offending row's name, in its own form (review R2)
      reveal('oppgaver', taskProblem.uid ? `#${taskProblem.uid}-name` : 'input', taskProblem.msg, 'task');
      return false;
    }
    // the button that opened the editor promised a clock time; saving without it is not a save
    // (issue #81: it closed with "Lagret" and the row stayed in review)
    const want = focus?.punch;
    const missing =
      want && (want !== 'in' && !d.punchOut ? 'out' : want !== 'out' && !d.punchIn ? 'in' : null);
    if (missing) {
      toast(
        T(
          missing === 'in' ? 'Skriv inn tiden det ble stemplet inn.' : 'Skriv inn tiden det ble stemplet ut.',
        ),
      );
      setShiftOpen(true);
      setPunchOpen(true);
      punchInput(missing)?.focus(); // the fold is already open: the field exists
      return false;
    }
    try {
      if (badTime) {
        // the field says why under itself; the save only points at it (#272 A-S01)
        toast(T('Skriv klokkeslettet som tt:mm, for eksempel 07:30.'));
        bodyRef.current?.querySelector<HTMLElement>('input[data-time][aria-invalid=true]')?.focus();
        return false;
      }
      if (badDate) {
        reveal('vakt', '.date-field input', T('Skriv en gyldig dato, for eksempel 16.06.2027.'));
        return false;
      }
      if ('problem' in built) {
        // the inline warning beside the clock times has said so all along; Stempling opens on it
        reveal('stempling', 'input[aria-invalid=true]', built.problem);
        return false;
      }
      const body: ShiftWrite = built;
      // a registration has no plan of its own: its times are the clock times, so Kontroll does not
      // measure worked time against the shift type's default hours (run B, 2026-10-22)
      if (registration && body.punch_in_time && body.punch_out_time) {
        body.start_time = body.punch_in_time;
        body.end_time = body.punch_out_time;
      }
      if (needsReason) {
        if (reason.trim().length < 3) {
          // a moved day asks under Vakt, a corrected time under Stempling
          reveal(
            movesDay || d.anchor ? 'vakt' : 'stempling',
            '[data-reason]',
            T('Skriv hvorfor registrert tid endres.'),
            'reason',
          );
          return false;
        }
        body.change_reason = reason.trim();
      }
      // the pause, checked beside its fields and sent with the shift
      if (lunchShift) {
        const lw = lunchWrite(lunchShift, pause);
        if (lw.problem) {
          setLunchProblem(lw.problem);
          reveal(
            'vakt',
            `[data-testid=vlunch] ${lw.problem.field === 'at' ? 'input[data-time]' : 'input[type=number]'}`,
            lw.problem.text,
          );
          return false;
        }
        if (lw.body) Object.assign(body, lw.body);
      }
      if (movesDay) body.move_clocked = true;
      if (farClock) body.clock_far_ok = true;
      if (canApprove && draft?.is_approved && corrects) body.is_approved = false;
      // sent only when a task definition was staged; omitted, the API keeps the stored rows
      // (admin/shiftController.js:820). A time-only edit therefore never touches a task
      const taskRows = canEditTasks ? tasksBodyOf(tasks ?? [], edits) : undefined;
      if (taskRows !== undefined) body.taskAssignments = taskRows;
      if (!isNew && submit) {
        // the page's lock says what happened, saved, refused or unknown; the editor stays open
        // unless it was saved, so nothing typed is lost
        let saved: ApiShift | undefined;
        const ok = await submit(d.id as number, async () => {
          saved = await w.update.mutateAsync({ id: d.id as number, body });
        });
        if (!ok) return false;
        if (opts.approve && saved) act?.onApprove?.(true, saved);
        // what was saved, said as it is: approved too, and who hears about a correction
        if (body.is_approved === false && draft?.is_approved)
          toast(T('Lagret. Godkjenningen er tatt bort, fordi tidene er endret.'));
        setDirty('shift-drawer', null);
        onClose();
        return true;
      }
      // one write per opening, whichever editor holds it (R1): a second press, or the editor
      // remounted across the phone breakpoint, waits for the first
      const mine = draft;
      if (!mine || outs.has(mine)) return false;
      // a create is one named operation (#157), remembered in this browser until its answer is known
      const sent: ShiftWrite = isNew ? { ...body, op_id: newOpId() } : body;
      if (isNew && tenant?.id != null) rememberOp(tenant.id, sent as ShiftWrite & { op_id: string });
      setOut(mine, { state: 'out', body: sent });
      let saved: ApiShift | undefined;
      try {
        saved = await (isNew
          ? w.create.mutateAsync(sent)
          : w.update.mutateAsync({ id: d.id as number, body }));
      } catch (e) {
        if (refused(e)) {
          setOut(mine, null);
          if (sent.op_id) forgetOp(sent.op_id);
          throw e; // said below, the draft kept
        }
        // no answer is not a refusal: the shift may exist (F1); only the same operation is offered
        setOut(mine, { state: 'unknown', body: sent });
        toast(isNew ? T(UNKNOWN_NEW) : T(UNKNOWN));
        return false;
      }
      setOut(mine, null);
      if (sent.op_id) forgetOp(sent.op_id);
      if (opts.approve && !isNew && saved) act?.onApprove?.(true, saved);
      else
        toast(
          isNew
            ? registration
              ? T('Registreringen er lagret')
              : T('Vakten er opprettet som utkast')
            : T('Lagret'),
        );
      // only the opening that sent it closes (F2): after Avbryt and another shift, that editor stays
      if (showing.has(mine)) {
        // saved: nothing is unsaved any more, so closing does not ask about leaving it
        setDirty('shift-drawer', null);
        onClose();
      }
      return true;
    } catch (e) {
      toast(T('Kunne ikke lagre: {message}', { message: (e as Error).message }));
      return false;
    }
  }
  saveRef.current = save;

  /**
   * After an unknown outcome. A create sends the same operation again (#157): the server answers
   * with the shift the first request made, or makes it now, once; a refusal frees the editor with
   * the draft kept; no answer again leaves it unknown. An edit reads the day, which frees it as the
   * page's lock does. Only the latest check for the opening that sent it acts, and only that
   * opening's editor closes.
   */
  async function check() {
    const mine = draft;
    const o = mine ? outs.get(mine) : undefined;
    if (!mine || !d || o?.state !== 'unknown' || o.checking) return;
    const gen = (o.gen ?? 0) + 1;
    setOut(mine, { ...o, checking: true, gen });
    // only the latest check for this opening, while it is still unknown, may act
    const current = () => {
      const cur = outs.get(mine);
      return cur?.state === 'unknown' && cur.gen === gen ? cur : null;
    };
    const opId = o.body.op_id;
    if (opId) {
      try {
        await w.create.mutateAsync(o.body);
      } catch (e) {
        const cur = current();
        if (!cur) return;
        if (refused(e)) {
          setOut(mine, null);
          forgetOp(opId);
          toast(refusedText(e));
        } else {
          setOut(mine, { ...cur, checking: false });
          toast(T('Fikk fortsatt ikke svar. Prøv igjen om litt.'));
        }
        return;
      }
      forgetOp(opId);
      if (!current()) return;
      setOut(mine, null);
      toast(T('Vakten er lagret.'));
      if (showing.has(mine)) {
        setDirty('shift-drawer', null);
        onClose();
      }
      return;
    }
    let shifts: ApiShift[] | null = null;
    try {
      const day = oslo(o.body.start_time ?? '')?.date ?? d.date;
      const r = await get<{ shifts: ApiShift[] }>(
        `/admin/shifts/date-range?start_date=${day}&end_date=${addDays(day, 1)}`,
      );
      shifts = r.shifts ?? [];
    } catch {
      /* said below */
    }
    const cur = current();
    if (!cur) return;
    if (!shifts) {
      setOut(mine, { ...cur, checking: false });
      toast(T('Kunne ikke lese planen. Prøv igjen.'));
      return;
    }
    void invalidateShifts(qc);
    setOut(mine, null);
    toast(T('Planen er lest på nytt. Sjekk vakten før du lagrer igjen.'));
  }

  const field = (label: string, control: React.ReactNode) => (
    <div className="fld">
      <span className="lbl">{T(label)}</span>
      {control}
    </div>
  );
  const PUNCH_FIELDS = ['punchIn', 'punchOut', 'lunchIn', 'lunchOut'] as const;
  const time = (
    k: 'from' | 'to' | 'punchIn' | 'punchOut' | 'lunchIn' | 'lunchOut',
    label: string,
    // the word shown over the field (#154 VF-07); the full label stays its name, which holds this word
    shown: string,
  ) => {
    // seeing what was clocked and correcting it are separate permissions on the server, so
    // a reader gets the value and not an input that would be refused
    // the plan's own times are the planner's (schedule.manage); a reader opening the shift reads them
    const readOnly = (PUNCH_FIELDS as readonly string[]).includes(k) ? !canEditPunches : !canPlan;
    return (
      <div className="vtl">
        <span className="vtl-t" aria-hidden="true">
          {T(shown)}
        </span>
        {/* the app's time field beside its date field, not the browser's «--:--» (#272 A-13) */}
        <TimeInput
          className="inp"
          aria-label={T(label)}
          value={d[k]}
          readOnly={readOnly}
          disabled={readOnly}
          {...(punchProblem && (PUNCH_FIELDS as readonly string[]).includes(k)
            ? { 'aria-invalid': true, 'aria-describedby': 'vbad-punch' }
            : {})}
          onValueChange={(v) => set(k, v)}
          onInvalidChange={timeBad(k)}
        />
      </div>
    );
  };

  const clockDay = (k: 'in' | 'out', label: string) => {
    const field = k === 'in' ? 'punchInDay' : 'punchOutDay';
    const shown = k === 'in' ? d.punchIn : d.punchOut;
    const iso = 'problem' in built ? null : k === 'in' ? built.punch_in_time : built.punch_out_time;
    const value = d[field] ?? (iso ? (oslo(iso)?.date ?? d.date) : d.date);
    return (
      <div className="vtl">
        <span className="vtl-t" aria-hidden="true">
          {T(label)}
        </span>
        {/* the app's date field, as «Dato» above, not the browser's own (#272 A-13) */}
        <DateInput
          className="inp"
          aria-label={T(label)}
          value={value}
          readOnly={!canEditPunches || !shown}
          disabled={!canEditPunches || !shown}
          onValueChange={(v) => validPeriod(v, false) && setD({ ...d, [field]: v })}
        />
      </div>
    );
  };
  const name = isNew
    ? registration
      ? T('Ny registrering')
      : T('Ny vakt')
    : user
      ? fullName(user)
      : T('Ledig vakt');
  // «12. okt., 10:00–18:00», as the reference's header says it
  const when = `${Number(d.date.slice(8, 10))}. ${(MONTHS[Number(d.date.slice(5, 7)) - 1] ?? '').slice(0, 3)}., ${d.from}–${d.to}`;
  // a new shift says who it is for beside the time; a saved one has the name as its heading
  const whenWho = isNew ? `${when}, ${user ? fullName(user) : T('ledig vakt')}` : when;
  const remove = () => {
    const r = askDeleteReason({ punch_in_time: d.punchIn0, punch_out_time: d.punchOut0 });
    if (r === null) return;
    onDelete(d.id as number, r);
  };
  const busy = !!act?.busy;
  const planEditable = canEditTasks && planning;
  // the hours' state, for the footer and the Stempling marker (issue #153 SP04, SP06, SP09, SP10)
  // the real end: an end at or before the start is the next day's, the way bodyOf builds it (review F3)
  const ended = !isNew && endsBy(d, Date.now());
  const missingTime = !isNew && canSeePunches && ended && !punched;
  // the plan offered for empty clock fields once the shift has ended (SD13c)
  const offer = !isNew && canEditPunches && ended;
  const plannedPause = (() => {
    if (!pause.at || !pause.len) return lunch ?? null;
    const [h, m] = pause.at.split(':').map(Number);
    const e = ((h as number) * 60 + (m as number) + Number(pause.len)) % 1440;
    return {
      from: pause.at,
      to: `${String(Math.floor(e / 60)).padStart(2, '0')}:${String(e % 60).padStart(2, '0')}`,
    };
  })();
  // one «Fyll inn som planlagt» for every empty clock field, the pause from the planned one (owner 2026-10-26)
  const asPlanned = {
    punchIn: d.punchIn || d.from,
    punchOut: d.punchOut || d.to,
    lunchIn: d.lunchIn || plannedPause?.from || '',
    lunchOut: d.lunchOut || plannedPause?.to || '',
  };
  const canFill = offer && PUNCH_FIELDS.some((k) => d[k] !== asPlanned[k]);
  const punchCodes = (e: Entry) => /^punch_|^open$|lunch|break/.test(e.code);
  const planEntries = entries.filter((e) => !punchCodes(e));
  const timeEntries = entries.filter(punchCodes);
  const approvedBefore = !!draft?.is_approved;
  // «Registrer tid»: Stempling opens on the first missing time, focused
  const registerTime = () => {
    openSection('stempling', true);
    requestAnimationFrame(() => punchInput(!d.punchIn ? 'in' : 'out')?.focus());
  };
  const marker = (list: Entry[], extra?: string) =>
    list.length || extra ? (
      <span
        className={`vmark ${list.some((e) => e.sev === 'd') ? 'd' : 'w'}`}
        data-testid="vmark"
        title={list.map((e) => ruleText(t, e)).join(', ')}
      >
        <Icon name={list.some((e) => e.sev === 'd') ? 'stop' : 'warn'} />
        {extra ?? (list.length === 1 ? T('1 varsel') : T('{n} varsler', { n: list.length }))}
      </span>
    ) : null;
  // each one in words as well as colour: Brudd or Advarsel, then what it is (SP02, SP30)
  const explainRows = (list: Entry[]) =>
    list.map((e) => (
      <div className={`al ${e.sev === 'd' ? 'dgr' : 'warn'}`} key={`${e.code}${e.args?.hours ?? ''}`}>
        <span>
          <Icon name={e.sev === 'd' ? 'stop' : 'warn'} />
        </span>
        <span>
          <b>{e.sev === 'd' ? T('Brudd') : T('Advarsel')}</b> {ruleText(t, e)}
        </span>
      </div>
    ));
  const explain = (list: Entry[]) =>
    list.length ? (
      <div className="vwarns" data-testid="vwarn">
        {explainRows(list)}
      </div>
    ) : null;
  const around = act?.around;
  const stored = act?.shift;
  const lateTasks = (tasks ?? []).filter((a) => {
    if (!a.required || a.status === 'completed' || edits.changed[a.id] === null) return false;
    const end =
      a.has_time_range && a.end_time
        ? osloISO(a.end_time.slice(0, 5) < d.from ? addDays(d.date, 1) : d.date, a.end_time.slice(0, 5))
        : osloISO(d.to <= d.from ? addDays(d.date, 1) : d.date, d.to);
    return Date.parse(end) <= Date.now();
  }).length;
  // what will be on the shift: stored rows not being removed, and added rows that have a name
  const taskCount =
    (tasks ?? []).filter((a) => edits.changed[a.id] !== null).length +
    edits.added.filter((r) => r.name.trim()).length;

  const vakt = (
    <>
      {locked && (
        <output className="al wrn" data-testid="vedit-locked">
          <span>
            {T(
              'Vakten venter på svar på en annen endring. Les på nytt før du lagrer; det du har skrevet blir stående.',
            )}
          </span>
          {onReread && (
            <Button small onClick={onReread}>
              <Icon name="refresh" />
              {T('Les på nytt')}
            </Button>
          )}
        </output>
      )}
      {explain(planEntries)}
      {planEditable && stored && !stored.published && entries.length > 0 && (
        <p className="hint">{T('Varsler stopper ikke publisering. Se over dem først.')}</p>
      )}
      {around?.absence && (
        <div className="vbox" data-testid="vabsence">
          <span>
            {T('Fravær denne dagen')}: <b>{around.absence.absence_type?.name ?? T('fravær')}</b>
            {around.absence.status === 'pending' ? `, ${T('venter på svar')}` : ''}
          </span>
          {act?.onOpenAbsence && (
            <Button small onClick={() => act.onOpenAbsence?.(around.absence as ApiAbsence)}>
              {T('Åpne fraværet')}
            </Button>
          )}
        </div>
      )}
      {around?.offer && (
        <div className="vbox" data-testid="vrequest">
          <span>
            {T('{who} vil gi bort vakten', { who: fullName(around.offer.offeredByUser) })}
            {around.offer.acceptedByUser
              ? `, ${T('{who} tar den', { who: fullName(around.offer.acceptedByUser) })}`
              : ''}
            .
          </span>
          {planEditable && act?.onDecide && (
            <span className="vacts">
              <Button
                small
                tone="pri"
                onClick={() => act.onDecide?.('offer', around.offer?.id as number, true)}
              >
                <Icon name="check" />
                {T('Godkjenn')}
              </Button>
              <Button small onClick={() => act.onDecide?.('offer', around.offer?.id as number, false)}>
                {T('Avslå')}
              </Button>
            </span>
          )}
        </div>
      )}
      {around?.swap && (
        <div className="vbox" data-testid="vrequest">
          <span>
            {T('{a} og {b} vil bytte vakter', {
              a: fullName(around.swap.offeredByUserOne),
              b: fullName(around.swap.offeredByUserTwo),
            })}
            .
          </span>
          {planEditable && act?.onDecide && (
            <span className="vacts">
              <Button
                small
                tone="pri"
                onClick={() => act.onDecide?.('swap', around.swap?.id as number, true)}
              >
                <Icon name="check" />
                {T('Godkjenn')}
              </Button>
              <Button small onClick={() => act.onDecide?.('swap', around.swap?.id as number, false)}>
                {T('Avslå')}
              </Button>
            </span>
          )}
        </div>
      )}
      {/* one under the other: side by side at a 340 px panel the type was cut to «Dag 10:00-18:0» (#154 VF-07) */}
      <>
        {field(
          'Vakttype',
          <TypeSelect value={d.shift_type_id ?? null} types={types} disabled={!canPlan} onChange={setType} />,
        )}
        {/* the person is known on a held shift: changing them is asked for, not offered (SP17) */}
        {!isNew && d.user_id != null && !pickPerson ? (
          <div className="fld vperson">
            <span className="lbl">{T('Ansatt')}</span>
            <span className="vpersonacts">
              {!clocked && planEditable && (
                <button type="button" className="lnk" onClick={() => setPickPerson(true)}>
                  <Icon name="userPlus" />
                  {T('Bytt medarbeider')}
                </button>
              )}
              {!clocked && planEditable && act?.onCover && (
                <button type="button" className="lnk" onClick={act.onCover}>
                  <Icon name="users" />
                  {T('Finn erstatter')}
                </button>
              )}
              {/* a lock where the person would be changed, the reason in its tooltip (owner 2026-10-25: the box
                  "takes space and focus") */}
              {/* the name and why it cannot change, in words (owner 2026-10-25: a bare lock "with no context");
                  the server refuses another person on a clocked shift: the hours are whoever clocked them */}
              {clocked && (
                <span className={lk.who}>
                  <b>{name}</b>
                  <span className={lk.lock} title={T('Stemplet: personen kan ikke byttes.')}>
                    <Icon name="lock" />
                    <span aria-hidden="true">{T('stemplet inn')}</span>
                    <span className="sr">{T('Stemplet: personen kan ikke byttes.')}</span>
                  </span>
                </span>
              )}
            </span>
          </div>
        ) : (
          field(
            'Ansatt',
            <select
              className="inp w"
              aria-label={T('Ansatt')}
              disabled={clocked || !canPlan}
              value={d.user_id ?? ''}
              onChange={(e) => set('user_id', e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">{T('Ledig vakt')}</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {fullName(u)}
                </option>
              ))}
            </select>,
          )
        )}
      </>
      {!isNew && d.user_id == null && planEditable && act?.onCover && (
        <p style={{ margin: '0 0 10px' }}>
          <Button small onClick={act.onCover} data-testid="vedit-cover">
            <Icon name="users" />
            {T('Finn erstatter')}
          </Button>
        </p>
      )}
      {/* the day of a clocked shift: a lock and «Endre» on the label's line, the reason in the lock's tooltip; once
          unlocked, one line says what follows (owner 2026-10-25, "Lock icon in the fields") */}
      <div className="fld" data-testid={clocked && d.punchIn0 ? 'clocked-lock' : undefined}>
        <span className={`lbl ${lk.row}`}>
          {T('Dato')}
          {clocked && d.punchIn0 && !unlocked && (
            <span className={lk.acts}>
              <span
                className={lk.lock}
                title={`${T('Stemplet {when}. Dag og ansatt er låst.', { when: stamp(d.punchIn0) })}${d.is_approved ? ` ${T('Fjern godkjenningen for å endre dagen.')}` : ''}`}
              >
                <Icon name="lock" />
                <span aria-hidden="true">{T('Stemplet {t}', { t: oslo(d.punchIn0)?.time ?? '' })}</span>
                <span className="sr">
                  {T('Stemplet {when}. Dag og ansatt er låst.', { when: stamp(d.punchIn0) })}{' '}
                  {d.is_approved ? T('Fjern godkjenningen for å endre dagen.') : null}
                </span>
              </span>
              {!d.is_approved && (
                <button type="button" className={`lnk ${lk.edit}`} onClick={() => setUnlocked(true)}>
                  {T('Endre')}
                  <span className="sr"> {T('dag likevel')}</span>
                </button>
              )}
              {/* approved: the way to edit is here too, not only under Flere handlinger (owner 2026-10-25) */}
              {d.is_approved && canApprove && act?.onApprove && (
                <button type="button" className={`lnk ${lk.edit}`} onClick={() => act.onApprove?.(false)}>
                  {T('Fjern godkjenning')}
                </button>
              )}
            </span>
          )}
        </span>
        <DateInput
          className="inp w"
          aria-label={T('Dato')}
          disabled={(clocked && !unlocked) || !canPlan}
          value={dateText ?? d.date}
          // only a real date reaches the draft: every instant the editor builds, on each render, is
          // made from it, and «16.06.20» half typed made an invalid one and threw (#154 VF-07); what is
          // typed meanwhile is the session's dateText (UI01-15)
          onValueChange={(value) => {
            if (validPeriod(value, false)) {
              setDateText(null);
              // the end date follows the day (a set one is kept as its distance from it)
              setD({
                ...d,
                date: value,
                ...(d.toDay ? { toDay: addDays(value, dayGap(d.date, d.toDay)) } : {}),
              });
            } else setDateText(value);
          }}
        />
        {clocked && d.punchIn0 && unlocked && (
          <span className={lk.note}>
            <span className="sr">
              {T('Stemplet {when}. Dag og ansatt er låst.', { when: stamp(d.punchIn0) })}{' '}
            </span>
            {T('Velg ny dag. Stemplingen flyttes med, og du må skrive hvorfor.')}
          </span>
        )}
      </div>
      {offDay ? (
        <div className="al warn" data-testid="punch-off-day" style={{ margin: '0 0 10px' }}>
          <span>
            <Icon name="warn" />
          </span>
          <span>
            {T('Stemplingen er {punch}, vakten er {shift}.', {
              punch: fmtD(punchDay ?? d.date),
              shift: fmtD(d.date),
            })}{' '}
            <button type="button" className="lnk" onClick={() => setD({ ...d, anchor: true })}>
              {T('Flytt stemplingen til vaktens dag')}
            </button>
          </span>
        </div>
      ) : null}
      {movesDay || d.anchor ? (
        <label className="fld2" style={{ margin: '0 0 10px' }}>
          <span>
            {d.anchor ? T('Hvorfor flyttes stemplingen?') : T('Hvorfor flyttes den stemplede vakten?')}{' '}
            <span className="req">*</span>
          </span>
          <input
            className="inp"
            aria-label={T('Begrunnelse for flyttingen')}
            data-reason
            {...badProps('reason')}
            value={reason}
            maxLength={500}
            placeholder={T('F.eks. vakten var registrert på feil dag')}
            onChange={(e) => setReason(e.target.value)}
            required
          />
          {badMsg('reason')}
        </label>
      ) : null}
      {!registration &&
        field(
          'Tid',
          <>
            <div className="two">
              {time('from', 'Fra', 'Fra')}
              {time('to', 'Til', 'Til')}
            </div>
            {/* the end's date under «Til»: a night 22:00 to 03:00 ends the next day (BK, 2026-10-29). The start's date
                is «Dato» above, so it is not repeated here as a greyed «Fra dato» (#272 A-R12) */}
            <div className="two" data-testid="plan-days">
              <div className="vtl" style={{ gridColumn: 2 }}>
                <span className="vtl-t" aria-hidden="true">
                  {T('Til dato')}
                </span>
                <DateInput
                  className="inp"
                  aria-label={T('Til dato')}
                  value={d.toDay ?? endDayOf(d.date, d.from, d.to)}
                  min={d.date}
                  disabled={(clocked && !unlocked) || !canPlan}
                  onValueChange={(v) => validPeriod(v, false) && set('toDay', v)}
                />
              </div>
            </div>
          </>,
        )}
      {/* the pause right under the times (owner 2026-10-25, as Sling and Quinyx), not behind Aktiviteter */}
      {stored && act?.lunch && (
        <LunchRow
          s={stored}
          editable={planEditable}
          minutes={act.lunch.minutes}
          value={pause}
          onChange={(v) => {
            setPause(v);
            setLunchProblem(null);
          }}
          problem={lunchProblem}
          onInvalidChange={timeBad('lunch')}
        />
      )}
      {!registration && d.from && d.to && (
        <p className="hint" data-testid="vedit-length">
          {T('{h} timer', { h: num(planHours(d.from, d.to)) })}
          {/* an end at or before the start is the next day's: said with its date, not left to be worked out */}
          {d.to <= d.from && d.date
            ? `, ${T('slutter {date}, neste dag', { date: fmtD(addDays(d.date, 1)) })}`
            : ''}
        </p>
      )}
      {isNew &&
        match &&
        (() => {
          // Legg til vakt (#131): who fits at this day and these times, the reason in one line
          const list = match(d)
            .filter((o) => o.kind !== 'blocked')
            .slice(0, 3);
          if (!list.length) return null;
          // the approved planning option rows, for people (#154 UI01-10): one radio per person
          return (
            <PersonOptions
              legend={T('Hugin foreslår')}
              name={`vmatch-${d.date}-${d.from}`}
              testid="vmatch"
              options={list.map((o) => ({
                id: o.userId,
                name: o.name,
                reason: o.reason,
                review: o.kind === 'review',
              }))}
              selected={d.user_id}
              onSelect={(id) => set('user_id', id)}
            />
          );
        })()}
      {stored && around && <Overlap s={stored} others={around.sameDay} />}
    </>
  );

  const stempling = (
    <>
      {missingTime && (
        <p className="vsecnote warn" data-testid="vedit-missing">
          {T('Stempling mangler. Registrer faktisk inn- og utstempling.')}
        </p>
      )}
      {explain(timeEntries)}
      <div className="fld">
        <div className="two">
          {time('punchIn', 'Stemplet inn', 'Inn')}
          {time('punchOut', 'Stemplet ut', 'Ut')}
        </div>
        {(d.punchIn || d.punchOut) && (
          // the dates of the two clocks, settable: a clock-in or clock-out pressed on another day is moved here
          // (owner 2026-10-28: "users have clocked in for multiple days and there is no way for managers to change that")
          <div className="two" data-testid="punch-days">
            {clockDay('in', 'Dato inn')}
            {clockDay('out', 'Dato ut')}
          </div>
        )}
        {outDayNote}
      </div>
      {field(
        'Pause',
        <div className="two">
          {time('lunchIn', 'Pause fra', 'Fra')}
          {time('lunchOut', 'Pause til', 'Til')}
        </div>,
      )}
      {canFill && (
        // fills the empty fields only; the manager saves, so nothing unworked is approved by itself (owner 18.10)
        <Button className="vfill" data-testid="vedit-as-planned" onClick={() => setD({ ...d, ...asPlanned })}>
          <Icon name="clock" />
          {T('Fyll inn som planlagt')}
        </Button>
      )}
      {punchProblem && (
        <div className="al dgr" role="alert" data-testid="punch-problem" id="vbad-punch">
          <span>
            <Icon name="stop" />
          </span>
          {/* what is wrong, and the way back: the stored times are untouched until a valid save (#149 V3) */}
          <span>
            {punchProblem}{' '}
            {T(
              'Ingenting er lagret. Rett tiden, eller lukk og forkast endringen for å beholde de lagrede tidene.',
            )}
          </span>
        </div>
      )}
      {(corrects || farClock) && !movesDay && !d.anchor && (
        <label className="fld2" style={{ marginTop: 8 }}>
          <span>
            {corrects
              ? T('Hvorfor endres registrert tid?')
              : T('Stemplet inn er mer enn 12 timer fra planlagt start. Hvorfor?')}{' '}
            <span className="req">*</span>
          </span>
          <input
            className="inp"
            data-reason
            {...badProps('reason')}
            value={reason}
            maxLength={500}
            placeholder={T('F.eks. glemte å stemple ut, bekreftet på telefon')}
            onChange={(e) => setReason(e.target.value)}
            required
          />
          {badMsg('reason')}
        </label>
      )}
      {approvedBefore && corrects && (
        <p className="vsecnote warn" data-testid="vedit-reapprove">
          {canApprove
            ? T('Timene er godkjent. Lagrer du rettingen, må den godkjennes på nytt.')
            : T('Timene er godkjent. Be noen som kan godkjenne timer om å rette dem.')}
        </p>
      )}
      {punched && worked != null && (
        <p className="hint">
          {T('Timer etter pause (hele vakten)')} <b>{num(worked)} t</b>
        </p>
      )}
      {canEditPunches && (
        <p className="hint">
          {T(
            'Tider du skriver her erstatter stemplingen. Den gamle tiden, hvem som endret og hvorfor blir lagret i historikken.',
          )}
        </p>
      )}
    </>
  );

  const oppgaver = (
    <>
      {isNew && <p className="hint">{T('Opprettes som utkast. Du publiserer når planen er klar.')}</p>}
      {canEditTasks ? (
        <>
          <TaskEditor
            tasks={tasks ?? []}
            edits={edits}
            onChange={setEdits}
            from={d.from}
            to={d.to}
            // the pause as it stands in Vakt, a moved one included: the timeline keeps it free
            lunch={plannedPause}
            refused={bad?.at === 'task'}
            date={d.date}
            shiftId={d.id}
            userId={d.user_id}
            // the pause moves on the timeline as in Vakt's field, saved with the shift (#202)
            onLunch={
              lunchShift && pause.at && pause.len
                ? (at) => {
                    setPause({ at, len: pause.len });
                    setLunchProblem(null);
                  }
                : undefined
            }
          />
          {!shiftOpen && <Alongside d={d} edits={edits} others={others} users={users} />}
        </>
      ) : tasks && tasks.length > 0 ? (
        <Tasks tasks={tasks} />
      ) : (
        <p className="hint">{T('Ingen aktiviteter på vakten.')}</p>
      )}
      {stored && around && planEditable && act?.onMoveTask && (
        <TaskMoves s={stored} others={around.sameDay} onMove={act.onMoveTask} busy={() => busy} />
      )}
      {stored && around && planEditable && act?.onBoundary && (
        <Edges s={stored} others={around.sameDay} onBoundary={act.onBoundary} busy={() => busy} />
      )}
    </>
  );

  const notat = (
    <>
      {field(
        'Notat',
        <input
          className="inp w"
          aria-label={T('Notat')}
          disabled={!canPlan}
          value={d.description}
          placeholder={T('Kort notat til vakten')}
          onChange={(e) => set('description', e.target.value)}
        />,
      )}
      {projectsOn && d.id != null && canEditPunches && (
        <ProjectField key={d.id} shiftId={d.id} initial={d.project_id ?? null} />
      )}
      {projectsOn &&
        d.id == null &&
        field(
          projectWord.one,
          <select
            className="inp w"
            aria-label={projectWord.one}
            data-testid="new-shift-project"
            {...badProps('project')}
            value={d.project_id ?? ''}
            onChange={(e) => set('project_id', e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">{projectRequired ? T('Velg …') : projectWord.none}</option>
            {projects
              .filter((p) => p.active)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {projectLabel(p)}
                </option>
              ))}
          </select>,
        )}
      {badMsg('project')}
    </>
  );

  // Flere handlinger: one row each, icon and words, the destructive one apart (SP20, SP21)
  const row = (
    key: string,
    icon: IconName,
    label: string,
    onClick: () => void,
    opts: { danger?: boolean; writes?: boolean } = {},
  ) => (
    <button
      type="button"
      key={key}
      className={`vrow${opts.danger ? ' dgr' : ''}`}
      data-testid={`vrow-${key}`}
      // a write waits for the shift's lock; a row that writes nothing does not (retest R02)
      disabled={opts.writes !== false && (busy || locked)}
      onClick={onClick}
    >
      <Icon name={icon} />
      <span>{label}</span>
    </button>
  );
  const rows = [
    ...(planEditable && onDuplicate
      ? [row('dup', 'copy', T('Kopier til neste dag'), () => onDuplicate(d.id as number))]
      : []),
    ...(planEditable && d.user_id != null && act?.onFree
      ? [row('free', 'user', T('Gjør vakten ledig'), act.onFree)]
      : []),
    ...(planEditable && stored && !stored.published && act?.onPublish
      ? [row('pub', 'upload', T('Publiser vakten'), act.onPublish)]
      : []),
    ...(planEditable && stored?.published && act?.onUnpublish
      ? [row('unpub', 'eyeOff', T('Trekk publisering'), act.onUnpublish)]
      : []),
    ...(canApprove && approvedBefore && act?.onApprove
      ? [row('unappr', 'check', T('Fjern godkjenning'), () => act.onApprove?.(false))]
      : []),
    ...(d.user_id != null && act?.onAbsence
      ? [row('abs', 'calendarOff', T('Registrer fravær'), act.onAbsence)]
      : []),
    ...(act?.onPick ? [row('pick', 'square', T('Merk flere'), act.onPick, { writes: false })] : []),
  ];
  const flere = (
    <div className="vrows">
      {rows}
      {canEditTasks && (
        <>
          <hr />
          {row('del', 'trash', T('Slett vakten'), remove, { danger: true })}
        </>
      )}
    </div>
  );

  const section = (
    id: SectionId,
    icon: IconName,
    title: string,
    mark: React.ReactNode,
    content: React.ReactNode,
  ) => (
    <section className={`vsec${open.has(id) ? ' open' : ''}`} data-testid={`vsec-${id}`} key={id}>
      <button
        type="button"
        className="vsech"
        aria-expanded={open.has(id)}
        aria-controls={`vsec-${id}-${d.id ?? 'new'}`}
        onClick={() => openSection(id, !open.has(id))}
      >
        <Icon name={icon} />
        <span>{title}</span>
        <span className="sp" />
        {mark}
        <Icon name="chevronDown" />
      </button>
      {open.has(id) && (
        <div className="vsecb" id={`vsec-${id}-${d.id ?? 'new'}`}>
          {content}
        </div>
      )}
    </section>
  );
  // one question for every way out with unsaved work: the drawer's own close, or the page's exit
  // when this editor holds the only unsaved work. Two answers (owner, 2026-10-20): save or discard,
  // or keep editing when the change is a decision the generic save must not make
  // (this shift, or the lunch row inside it)
  const asking = asked;
  // a half-typed time is never offered as a save (#272 A-S01)
  const savable = (askClose || canSaveAll()) && !badTime;
  const answer = (c: Choice) => {
    if (askClose) {
      setAskClose(false);
      // saves as the footer does: closes on success, keeps every field on a refusal
      if (c === 'save') void save();
      else if (c === 'discard') onClose();
      return;
    }
    // the question closes before the save runs, so a refused save can focus its own field
    void answerLeaving(c, async () => {
      await new Promise((r) => setTimeout(r, 0));
      return saveAll();
    });
  };
  // a modal over the page, not a box in the column (owner 2026-10-25: "confusing to keep in sidebar"); the
  // backdrop is outside noteRef, so a click on it is "keep editing" through stayOnClick
  const note = asking
    ? createPortal(
        <div className="confirm vleavem">
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="vleave-t"
            aria-describedby="vleave-d"
            data-testid="leave"
            ref={noteRef}
            onKeyDown={(e) => {
              if (e.key !== 'Escape') return;
              e.stopPropagation(); // Escape answers this question, it does not close the editor
              answer('stay');
            }}
          >
            <div>
              <h3 id="vleave-t">{T('Vakten er ikke lagret')}</h3>
              <p id="vleave-d">
                {isNew ? whenWho : `${name}, ${when}`}
                {/* the summary holds the last good time; say the field holds something else (#272 A-T06) */}
                {badTime && ` ${T('(ugyldig klokkeslett)')}`}
              </p>
              {writeOpen && (
                <p>{T('En lagring pågår eller har ukjent utfall. Sjekk vakten før du går videre.')}</p>
              )}
              <div className="df">
                {savable && !writeOpen ? (
                  <Button small tone="pri" autoFocus onClick={() => answer('save')}>
                    {T('Lagre vakten')}
                  </Button>
                ) : (
                  <Button small autoFocus onClick={() => answer('stay')}>
                    {T('Fortsett å redigere')}
                  </Button>
                )}
                <Button small ghost disabled={writeOpen} onClick={() => answer('discard')}>
                  {T('Forkast')}
                </Button>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )
    : null;
  const body = (
    <>
      {!inline && note}
      {section('vakt', 'calendar', T('Vakt'), marker(planEntries), vakt)}
      {(!isNew || registration) &&
        canSeePunches &&
        section(
          'stempling',
          'clock',
          T('Stempling'),
          missingTime ? marker(timeEntries, T('Mangler tid')) : marker(timeEntries),
          stempling,
        )}
      {section(
        'oppgaver',
        'list',
        T('Aktiviteter'),
        taskCount ? (
          <>
            {/* «N saker»: required activities past their end and not done, red, as the prototype */}
            {lateTasks > 0 && (
              <span className="vlate" data-testid="tasks-late">
                {lateTasks === 1 ? T('1 sak') : T('{n} saker', { n: lateTasks })}
              </span>
            )}
            <span className="vcount">{taskCount}</span>
          </>
        ) : null,
        oppgaver,
      )}
      {section('notat', 'document', T('Notat og prosjekt'), null, notat)}
      {/* the shift's log in a section of its own, not folded under the actions (owner 2026-10-25) */}
      {!isNew &&
        mayReadHistory &&
        (historyQ.data?.length ?? 0) > 0 &&
        section(
          'historikk',
          'refresh',
          T('Historikk'),
          <span className="vcount">{historyQ.data?.length}</span>,
          <History rows={historyQ.data ?? []} day={d.date} />,
        )}
      {!isNew &&
        (rows.length > 0 || canEditTasks) &&
        section('flere', 'more', T('Flere handlinger'), null, flere)}
      {act?.outcome && (
        <p aria-live="polite" className={`vout ${act.outcome.kind}`} data-testid="voutcome">
          {act.outcome.text}
          {act.outcome.kind === 'unknown' && onReread && (
            <>
              {' '}
              <button type="button" className="link" onClick={onReread}>
                <Icon name="refresh" />
                {T('Les på nytt')}
              </button>
            </>
          )}
        </p>
      )}
    </>
  );

  // the time row: what the hours are and the one step they need (SP04 to SP10)
  const timeRow =
    isNew || !canSeePunches ? null : missingTime ? (
      <div className="vtime" data-testid="vedit-time">
        <Icon name="clock" />
        <span>
          <b>{T('Stempling mangler')}</b>
          <small>{T('Fyll inn faktisk tid')}</small>
        </span>
        {canEditPunches && (
          <Button small onClick={registerTime} data-testid="vedit-register">
            <Icon name="pencil" />
            {T('Registrer tid')}
          </Button>
        )}
      </div>
    ) : punched ? (
      <div className="vtime" data-testid="vedit-time">
        <Icon name={approvedBefore && !corrects ? 'check' : 'clock'} />
        <span>
          <b>
            {d.punchIn}–{d.punchOut}
          </b>
          <small>
            {approvedBefore && !corrects
              ? T('Godkjent')
              : approvedBefore
                ? T('Endret etter godkjenning')
                : T('Ikke godkjent')}
          </small>
        </span>
        {/* approval is of the saved values (#149 V3, #264): with an edit the button saves first and
            approves the saved shift only when the save went through; an invalid or saving edit blocks
            it. Without the page's own approval (Timer approves beside the list) there is no button. */}
        {canApprove && act?.onApprove && (!approvedBefore || corrects) && !punchProblem && (
          <Button
            tone="pri"
            onClick={() => (dirty ? void save({ approve: true }) : act.onApprove?.(true))}
            disabled={busy || locked || inFlight || w.update.isPending || badTime}
            data-testid="vedit-approve"
          >
            <Icon name="check" />
            {dirty ? T('Lagre og godkjenn') : T('Godkjenn timer')}
          </Button>
        )}
      </div>
    ) : null;
  const approveShown =
    !!timeRow && punched && canApprove && !!act?.onApprove && (!approvedBefore || corrects) && !punchProblem;
  const footer = (
    <>
      {out === 'unknown' && (
        <output className="vsecnote warn" data-testid="vedit-unknown" style={{ display: 'block' }}>
          {d.id == null ? T(UNKNOWN_NEW) : T(UNKNOWN)}{' '}
          <button type="button" className="lnk" disabled={checking} onClick={() => void check()}>
            {d.id == null ? T('Sjekk status') : T('Sjekk planen')}
          </button>
        </output>
      )}
      {timeRow}
      <div className="vfootacts">
        {/* Avbryt first, the save last (UI consistency v1 CP-10) */}
        <Button
          ghost
          onClick={() => {
            // leaving does not take back what was sent (F2): say what happens to it
            if (out === 'out') toast(T('Lagringen er sendt. Vakten lagres hvis den går gjennom.'));
            else if (out === 'unknown') toast(T('Vi vet ikke om vakten ble lagret. Se etter den i planen.'));
            if (inline) onClose();
            else if (!askFirst()) onClose();
          }}
        >
          {isNew || canPlan || canEditPunches ? T('Avbryt') : T('Lukk')}
        </Button>
        {(isNew || canPlan || canEditPunches) && (
          // filled only once there is something to save: before an edit it read as the next step (#272 A-R12)
          <Button
            {...(approveShown || (!isNew && !dirty) ? {} : { tone: 'pri' as const })}
            onClick={() => void save()}
            disabled={
              locked || inFlight || w.create.isPending || w.update.isPending || !d.from || !d.to || badTime
            }
            data-testid="vedit-save"
          >
            {isNew ? (registration ? T('Lagre registrering') : T('Opprett utkast')) : T('Lagre endring')}
          </Button>
        )}
      </div>
    </>
  );
  const head = (
    <div className="vphd vedhd">
      <span className="av" aria-hidden="true">
        {user ? initialsOf(user) : <Icon name="user" />}
      </span>
      <div>
        <h3 id="vedit-t" ref={headRef} tabIndex={-1}>
          {name}
        </h3>
        <span className="vmeta">
          {whenWho}
          {!isNew && planning && (
            <span className={`vstate${d.published ? ' pub' : ''}`} data-testid="vstate">
              {d.published ? T('Publisert') : T('Utkast')}
            </span>
          )}
        </span>
      </div>
      <button type="button" className="x" aria-label={T('Lukk')} onClick={onClose}>
        <Icon name="close" />
      </button>
    </div>
  );
  const label = isNew ? name : T('Rediger vakt for {name}', { name });
  // a click anywhere else in the editor is "keep editing": the work is still there to finish
  const stayOnClick = (e: ReactPointerEvent) => {
    if (asking && !noteRef.current?.contains(e.target as Node)) answer('stay');
  };
  if (inline)
    return (
      <aside
        className="vpanel vedit"
        aria-label={label}
        data-testid="vedit"
        onPointerDownCapture={stayOnClick}
        onKeyDown={(e) => {
          // in the phone list the editor keeps the keyboard as the desktop panel does (#272 A-S03): Tab stays
          // inside, Escape closes (asking first when something is unsaved); the leave question is a portal
          // whose keys bubble here, so only keys from the editor itself count
          const box = e.currentTarget;
          if (!box.closest('.pphone') || !box.contains(e.target as Node) || e.defaultPrevented) return;
          if (e.key === 'Tab') return trapTab(e, box);
          if (e.key !== 'Escape') return;
          e.preventDefault();
          onClose();
        }}
      >
        {head}
        {note}
        <div className="vedbody" ref={bodyRef} onFocus={keepFocus}>
          {body}
        </div>
        <div className="vfoot">{footer}</div>
      </aside>
    );
  return (
    <Drawer
      open
      onClose={onClose}
      blockClose={askFirst}
      initialFocus={focus?.punch ? () => punchInput(focus.punch === 'in' ? 'in' : 'out') : undefined}
      title={name}
      sub={whenWho}
      footer={<div className="vfoot vfootd">{footer}</div>}
    >
      {/* the same ref as the inline editor's body, so the punch field can be found in both */}
      <div
        ref={bodyRef}
        aria-label={label}
        data-testid="vedit"
        onPointerDownCapture={stayOnClick}
        onFocus={keepFocus}
      >
        {body}
      </div>
    </Drawer>
  );
}

/**
 * The project a shift's hours go under (2026-09-29). Saved on its own, so the drawer's own save and
 * its checks are untouched; the person chose it when clocking in, the manager corrects it here.
 */
function ProjectField({ shiftId, initial }: { shiftId: number; initial: number | null }) {
  const projects = useProjects().data ?? [];
  const word = useProjectWord();
  const qc = useQueryClient();
  const [value, setValue] = useState<number | null>(initial);
  const [busy, setBusy] = useState(false);
  const change = async (next: number | null) => {
    setBusy(true);
    try {
      await setShiftProject(shiftId, next);
      setValue(next);
      toast(T('{what} lagret', { what: word.one }));
      invalidateShifts(qc);
    } catch (e) {
      toast(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <label className="fld" data-testid="shift-project">
      <span>{word.one}</span>
      <select
        className="inp w"
        disabled={busy}
        value={value ?? ''}
        onChange={(e) => change(Number(e.target.value) || null)}
      >
        <option value="">{word.none}</option>
        {projects.map((p) => (
          <option key={p.id} value={p.id} disabled={!p.active && p.id !== value}>
            {projectLabel(p)}
            {p.active ? '' : ` (${T('avsluttet')})`}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * "Andre i tidsrommet" for the activity being edited (handoff §3, retest R06): who else works in
 * that activity's interval, what they are doing then and whether they are at lunch. Folded by
 * default, so the editor stays one activity; it follows the times as they are typed.
 */
function Alongside({
  d,
  edits,
  others,
  users,
}: {
  d: DraftShift;
  edits: TaskEdits;
  others: ApiShift[];
  users: ApiUser[];
}) {
  // the row being worked on: the first one opened or added, with its times as typed
  const row =
    Object.values(edits.changed).find((x) => x?.has_time_range) ?? edits.added.find((x) => x.has_time_range);
  if (!row || !/^\d{2}:\d{2}/.test(row.start_time) || !/^\d{2}:\d{2}/.test(row.end_time)) return null;
  // clock times on the shift: one earlier than the shift's start is the next day
  const at = (hhmm: string) => {
    const x = Date.parse(osloISO(d.date, hhmm.slice(0, 5)));
    return hhmm.slice(0, 5) < d.from ? Date.parse(osloISO(addDays(d.date, 1), hhmm.slice(0, 5))) : x;
  };
  const a = at(row.start_time);
  const b0 = at(row.end_time);
  const b = b0 > a ? b0 : b0 + 864e5;
  const clockOf = (ms: number) => oslo(new Date(ms).toISOString())?.time ?? '';
  const near = others
    .filter((x) => x.id !== d.id && Date.parse(x.start_time) < b && Date.parse(x.end_time) > a)
    .sort((x, y) => Date.parse(x.start_time) - Date.parse(y.start_time));
  return (
    <details className="vgroup vdisc" data-testid="vedit-alongside">
      <summary>
        {T('Andre i tidsrommet {t} ({n})', { t: `${clockOf(a)}–${clockOf(b)}`, n: near.length })}
      </summary>
      {near.length === 0 ? (
        <p className="hint">{T('Ingen andre er planlagt da.')}</p>
      ) : (
        near.map((x) => {
          const day = oslo(x.start_time)?.date ?? d.date;
          const tAt = (hhmm: string, not: number) => {
            const v = Date.parse(osloISO(day, hhmm.slice(0, 5)));
            return v < not ? Date.parse(osloISO(addDays(day, 1), hhmm.slice(0, 5))) : v;
          };
          const busy = (x.taskAssignments ?? []).flatMap((t) => {
            if (!t.has_time_range || !t.start_time || !t.end_time) return [];
            const ta = tAt(t.start_time, Date.parse(x.start_time));
            const tb = tAt(t.end_time, ta + 60_000);
            return ta < b && tb > a
              ? [
                  `${t.shiftTask?.name ?? T('Aktivitet')} ${t.start_time.slice(0, 5)}–${t.end_time.slice(0, 5)}`,
                ]
              : [];
          });
          const la = x.planned_lunch_start ? Date.parse(x.planned_lunch_start) : null;
          const lunchHit =
            la != null && x.planned_lunch_minutes && la < b && la + x.planned_lunch_minutes * 60_000 > a;
          const who = x.user ?? users.find((u) => u.id === x.user_id);
          return (
            <div className="vcand" key={x.id}>
              <span>
                <b>{who ? fullName(who) : T('Ledig vakt')}</b>
                <small>
                  {shiftSpan(x.start_time, x.end_time)}
                  {busy.length ? `, ${busy.join(', ')}` : ''}
                  {lunchHit
                    ? `, ${T('lunsj {t}', { t: `${clockOf(la)}–${clockOf(la + (x.planned_lunch_minutes as number) * 60_000)}` })}`
                    : ''}
                </small>
              </span>
            </div>
          );
        })
      )}
    </details>
  );
}
